<template>
  <div :class="classes(n())">
    <!--
      triggerRef 是触发元素的包裹容器，display:inline-flex 使其尺寸与 slot 元素一致。
      position:relative 作为 arrow 和 content 的定位基准。
      currentPosition 类用于 CSS 控制箭头方向（border-color）。
    -->
    <div
      ref="triggerRef"
      :class="[classes(n('trigger')), innerVisible ? n(`--${currentPosition}`) : '']"
    >
      <slot />

      <template v-if="innerVisible">
        <!-- 箭头：通过 CSS border-color 控制方向，JS 控制 borderWidth 及位置 -->
        <div
          :class="[classes(n('arrow')), animClass]"
          :style="arrowStyle"
        />

        <!-- 气泡内容 -->
        <div
          ref="tooltipRef"
          :class="[
            classes(n('content')),
            closeIcon ? classes(n('content--close')) : '',
            animClass,
          ]"
          :style="contentStyle"
          role="tooltip"
        >
          <div :class="classes(n('content-inner'))">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div :class="classes(n('text'))" v-html="content" />
            <div
              v-if="closeIcon"
              :class="classes(n('close-hit'))"
              role="button"
              aria-label="Close tooltip"
              @click="handleClose"
            >
              <CloseOIcon
                :class="classes(n('close-icon'))"
                :width="32"
                :height="32"
              />
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, defineComponent, watch, onMounted, onBeforeUnmount, nextTick } from 'virtual-vue';
import { CloseOIcon } from '@tencent/wphk-design-icons';
import { props } from './props';
import { emits } from './emits';
import { createNamespace } from '../utils/components';
import { createZIndexManager, zIndexDefault } from '../use/useZIndex';
import { throttle } from '../shared/function';
import { inBrowser } from '../shared/is';
import type { TooltipArrowPosition } from './tooltip';

const { name, n, classes } = createNamespace('tooltip');

// border-triangle 的半尺寸（单位：CSS px），与 Less 中 @tooltip-arrow-size 保持一致
const ARROW_SIZE = 6.5;

const zIndexManager = createZIndexManager('tooltip', zIndexDefault.base);

type StyleMap = Record<string, string>;

export default defineComponent({
  name,
  components: { CloseOIcon },
  props,
  emits,
  setup(props, { emit }) {
    const triggerRef = ref<HTMLElement | null>(null);
    const tooltipRef = ref<HTMLElement | null>(null);

    const innerVisible = ref(false);
    // 当前箭头位置（可能因视口翻转而与 props.arrowPosition 不同）
    const currentPosition = ref<TooltipArrowPosition>(props.arrowPosition as TooltipArrowPosition);
    // 动画状态类名
    const animClass = ref('');
    // 气泡内容定位样式
    const contentStyle = ref<StyleMap>({});
    // 箭头定位样式（top/left/right + borderWidth）
    const arrowStyle = ref<StyleMap>({});
    // 防止对同一位置重复计算
    const lastCalcPosition = ref<string | null>(null);
    const zIndex = ref(zIndexManager.next());

    let hideAnimTimer: ReturnType<typeof setTimeout> | null = null;
    let autoCloseTimer: ReturnType<typeof setTimeout> | null = null;

    const clearTimers = () => {
      if (hideAnimTimer) { clearTimeout(hideAnimTimer); hideAnimTimer = null; }
      if (autoCloseTimer) { clearTimeout(autoCloseTimer); autoCloseTimer = null; }
    };

    // ─────────────────────────────────────────────────────────────
    // 定位计算
    // ─────────────────────────────────────────────────────────────

    /**
     * 将 triggerRef 容器的 position/top/left/width/height 同步为 slot 首元素的值。
     *
     * 核心原理：triggerRef 作为 arrow/content 的 absolute 定位基准。
     * 当 slot 元素是 position:static/relative 时，triggerRef 设为 position:relative，
     * 自然贴合元素尺寸。
     * 当 slot 元素是 position:fixed/absolute 时（如 fixed 定位的按钮），
     * triggerRef 也跟着设置相同的 position/top/left/width/height，
     * 使其在视口中与触发元素完全重叠，从而保证 tooltip 正确附着。
     */
    const syncContainerToTrigger = () => {
      if (!triggerRef.value) return;
      const firstChild = triggerRef.value.firstElementChild as HTMLElement | null;
      if (!firstChild) return;

      // LiteApp 中 getComputedStyle 可能返回 undefined（参考 tabs.vue），需降级处理
      const computedStyle = typeof getComputedStyle === 'function'
        ? getComputedStyle(firstChild)
        : undefined;
      const pos = computedStyle?.position;

      if (pos === 'fixed' || pos === 'absolute') {
        const rect = firstChild.getBoundingClientRect();
        Object.assign(triggerRef.value.style, {
          position: pos,
          top: `${rect.top}px`,
          left: `${rect.left}px`,
          width: `${rect.width}px`,
          height: `${rect.height}px`,
          zIndex: (computedStyle?.zIndex && computedStyle.zIndex !== 'auto') ? computedStyle.zIndex : '',
        });
      } else {
        Object.assign(triggerRef.value.style, {
          position: 'relative',
          top: '',
          left: '',
          width: '',
          height: '',
          zIndex: '',
        });
      }
    };

    /**
     * 获取 triggerRef 容器的视口矩形（在 syncContainerToTrigger 之后调用，
     * 此时容器已与触发元素重叠，getBoundingClientRect 等价于触发元素自身的 rect）。
     */
    const getTriggerRect = (): DOMRect | null =>
      triggerRef.value?.getBoundingClientRect() ?? null;

    const getTooltipRect = (): DOMRect | null =>
      tooltipRef.value?.getBoundingClientRect() ?? null;

    /**
     * 核心定位计算，将气泡和箭头定位到触发元素的对应方向。
     * 所有 left/top 值均为相对于 triggerRef 容器的绝对偏移（CSS px）。
     */
    const updatePosition = (position: string = currentPosition.value) => {
      // 同一位置跳过重复计算
      if (position === lastCalcPosition.value) return;
      lastCalcPosition.value = position;

      // 先同步容器位置，使 triggerRef 与触发元素完全重叠
      syncContainerToTrigger();

      const triggerRect = getTriggerRect();
      if (!triggerRect) return;

      // 临时设置 width: max-content 并清除内联 maxWidth，
      // 使 tooltip 按内容自然宽度渲染（CSS max-width 仍生效，确保多行文本正确换行）。
      // 测量后恢复为原值（而非 ''），避免 Vue 2 的 style diff 跳过写入。
      const tooltipEl = tooltipRef.value;
      const savedWidth = tooltipEl?.style.width ?? '';
      const savedMaxWidth = tooltipEl?.style.maxWidth ?? '';
      if (tooltipEl) {
        tooltipEl.style.width = 'max-content';
        tooltipEl.style.maxWidth = '';
      }
      const tooltipRect = getTooltipRect();
      if (tooltipEl) {
        tooltipEl.style.width = savedWidth;
        tooltipEl.style.maxWidth = savedMaxWidth;
      }
      if (!tooltipRect) return;

      const vw = document.documentElement.clientWidth;
      const { offset, margin, arrowDistance } = props;
      const [firstPart, lastPart] = position.split('-');

      let tooltipWidth = tooltipRect.width;

      let contentTop: string | null = null;
      let contentLeft: string | null = null;
      let contentRight: string | null = null;
      let contentTransform = '';
      let arrowTop: string | null = null;
      let arrowLeft: string | null = null;
      let arrowRight: string | null = null;

      // ── 主轴策略：决定气泡出现在触发元素的哪一侧 ──────────
      const primaryStrategies: Record<string, () => void> = {
        top: () => {
          contentTop = `${triggerRect.height + offset}px`;
          arrowTop = `${triggerRect.height + offset - 2 * ARROW_SIZE + 1}px`;
        },
        bottom: () => {
          contentTop = `${-offset - tooltipRect.height}px`;
          arrowTop = `${-offset - 1}px`;
        },
        left: () => {
          tooltipWidth = Math.min(tooltipWidth, Math.max(0, vw - triggerRect.right - offset - margin));
          contentLeft = `${triggerRect.width + offset}px`;
          contentTop = '50%';
          contentTransform = 'translateY(-50%)';
          arrowLeft = `${triggerRect.width + offset - 2 * ARROW_SIZE + 1}px`;
          arrowTop = `calc(50% - ${ARROW_SIZE}px)`;
        },
        right: () => {
          tooltipWidth = Math.min(tooltipWidth, Math.max(0, triggerRect.left - offset - margin));
          contentLeft = `${-(tooltipWidth + offset)}px`;
          contentTop = '50%';
          contentTransform = 'translateY(-50%)';
          arrowLeft = `${-offset - 1}px`;
          arrowTop = `calc(50% - ${ARROW_SIZE}px)`;
        },
      };

      // ── 副轴策略：top / bottom 时的水平对齐与溢出修正 ─────
      const alignStrategies: Record<string, () => void> = {
        left: () => {
          arrowLeft = `${arrowDistance}px`;
          const overflowRight = triggerRect.left + tooltipWidth > vw - margin;
          contentRight = overflowRight ? `${-(vw - triggerRect.right - margin)}px` : null;
          contentLeft = overflowRight ? null : '0px';
          tooltipWidth = overflowRight ? Math.min(vw - 2 * margin, tooltipWidth) : tooltipWidth;
        },
        right: () => {
          arrowRight = `${arrowDistance}px`;
          const overflowLeft = triggerRect.right - tooltipWidth < margin;
          contentLeft = overflowLeft ? `${margin - triggerRect.left}px` : null;
          contentRight = overflowLeft ? null : '0px';
          tooltipWidth = overflowLeft ? Math.min(vw - 2 * margin, tooltipWidth) : tooltipWidth;
        },
        center: () => {
          let rawLeft = triggerRect.width / 2 - tooltipWidth / 2;

          const leftOverflow = triggerRect.left + rawLeft < margin;
          rawLeft = leftOverflow ? margin - triggerRect.left : rawLeft;
          tooltipWidth = leftOverflow
            ? Math.min((triggerRect.left + triggerRect.width / 2 - margin) * 2, vw - 2 * margin)
            : tooltipWidth;

          const rightOverflow = rawLeft + tooltipWidth + triggerRect.left > vw - margin;
          tooltipWidth = rightOverflow
            ? (vw - triggerRect.left - margin - triggerRect.width / 2) * 2
            : tooltipWidth;
          rawLeft = rightOverflow ? triggerRect.width / 2 - tooltipWidth / 2 : rawLeft;

          contentLeft = `${rawLeft}px`;
          arrowLeft = `${rawLeft + tooltipWidth / 2 - ARROW_SIZE}px`;
        },
      };

      // 副轴路由：仅 top / bottom 需要二次对齐，left / right 在主轴中已完成全部定位
      const secondaryMap: Record<string, Record<string, () => void> | undefined> = {
        top: alignStrategies,
        bottom: alignStrategies,
      };

      primaryStrategies[firstPart]?.();
      secondaryMap[firstPart]?.[lastPart]?.();

      // bottom 方向修正：溢出处理可能收缩 tooltipWidth，导致文字换行、高度增加。
      // 以最终宽度重新测量高度，确保 contentTop 定位准确。
      if (firstPart === 'bottom' && tooltipEl && tooltipWidth < tooltipRect.width) {
        tooltipEl.style.width = `${tooltipWidth}px`;
        tooltipEl.style.maxWidth = `${tooltipWidth}px`;
        const finalHeight = tooltipEl.getBoundingClientRect().height;
        tooltipEl.style.width = savedWidth;
        tooltipEl.style.maxWidth = savedMaxWidth;
        contentTop = `${-offset - finalHeight}px`;
      }

      const zIndexStr = `${zIndex.value}`;

      contentStyle.value = {
        ...(contentTop !== null ? { top: contentTop } : {}),
        ...(contentLeft !== null ? { left: contentLeft } : {}),
        ...(contentRight !== null ? { right: contentRight } : {}),
        width: `${tooltipWidth}px`,
        maxWidth: `${tooltipWidth}px`,
        ...(contentTransform ? { transform: contentTransform } : {}),
        zIndex: zIndexStr,
      };

      arrowStyle.value = {
        ...(arrowTop !== null ? { top: arrowTop } : {}),
        ...(arrowLeft !== null ? { left: arrowLeft } : {}),
        ...(arrowRight !== null ? { right: arrowRight } : {}),
        borderWidth: `${ARROW_SIZE}px`,
        pointerEvents: 'none',
        zIndex: zIndexStr,
      };
    };

    /**
     * 检测气泡是否超出视口，超出时翻转 top ↔ bottom。
     * 在 nextTick 之后（DOM 已更新）调用。
     */
    const correctPosition = () => {
      if (!innerVisible.value) return;

      const tooltipRect = getTooltipRect();
      const triggerRect = getTriggerRect();
      if (!tooltipRect || !triggerRect) return;

      const [curPrimary, secondary] = currentPosition.value.split('-');
      const origPrimary = props.arrowPosition.split('-')[0];
      const vh = document.documentElement.clientHeight;
      const hasSpaceAbove = triggerRect.top > tooltipRect.height + props.offset;
      const hasSpaceBelow = vh - triggerRect.bottom > tooltipRect.height + props.offset;

      const flipPrimary = (newPrimary: string) => {
        currentPosition.value = `${newPrimary}-${secondary}` as TooltipArrowPosition;
        lastCalcPosition.value = null;
        updatePosition(currentPosition.value);
      };

      // 翻转规则表：[条件, 目标主轴方向]
      const rules: [boolean, string][] = [
        [tooltipRect.top < 0 && curPrimary === 'bottom', 'top'],
        [hasSpaceAbove && origPrimary === 'bottom', 'bottom'],
        [tooltipRect.bottom > vh && curPrimary === 'top', 'bottom'],
        [hasSpaceBelow && origPrimary === 'top', 'top'],
      ];

      const matched = rules.find(([condition]) => condition);
      matched && flipPrimary(matched[1]);
    };

    // ─────────────────────────────────────────────────────────────
    // 显示 / 隐藏
    // ─────────────────────────────────────────────────────────────

    const showTooltip = () => {
      if (innerVisible.value) return;

      clearTimers();
      zIndex.value = zIndexManager.next();
      currentPosition.value = props.arrowPosition as TooltipArrowPosition;
      lastCalcPosition.value = null;
      animClass.value = n('--fade-in');
      innerVisible.value = true;

      emit('update:visible', true);
      addGlobalListeners();

      nextTick(() => {
        updatePosition();
        correctPosition();

        if (props.duration > 0) {
          autoCloseTimer = setTimeout(hideTooltip, props.duration);
        }
      });
    };

    const hideTooltip = () => {
      if (!innerVisible.value) return;

      clearTimers();
      animClass.value = n('--fade-out');
      emit('update:visible', false);

      // 等动画结束再销毁 DOM
      hideAnimTimer = setTimeout(() => {
        innerVisible.value = false;
        animClass.value = '';
        currentPosition.value = props.arrowPosition as TooltipArrowPosition;
        lastCalcPosition.value = null;
        removeGlobalListeners();
      }, 200);
    };

    const handleClose = () => hideTooltip();

    // ─────────────────────────────────────────────────────────────
    // 事件处理
    // ─────────────────────────────────────────────────────────────

    const handleClickOutside = (e: MouseEvent) => {
      if (!innerVisible.value) return;
      // 有关闭图标或设置了自动消失时，不处理外部点击
      if (props.closeIcon || props.duration > 0) return;

      const isInsideTooltip = tooltipRef.value?.contains(e.target as Node);
      const isInsideTrigger = triggerRef.value?.contains(e.target as Node);
      if (!isInsideTooltip && !isInsideTrigger) {
        emit('clickOutside', e);
        hideTooltip();
      }
    };

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && innerVisible.value) hideTooltip();
    };

    const throttledCorrect = throttle(correctPosition, 200);

    // ─────────────────────────────────────────────────────────────
    // 全局事件按需注册（仅在 tooltip 可见时挂载，减少常驻监听）
    // ─────────────────────────────────────────────────────────────

    const addGlobalListeners = () => {
      if (!inBrowser()) return;
      document.addEventListener('click', handleClickOutside, true);
      document.addEventListener('scroll', throttledCorrect, { passive: true });
      document.addEventListener('keydown', handleKeydown);
    };

    const removeGlobalListeners = () => {
      if (!inBrowser()) return;
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('scroll', throttledCorrect);
      document.removeEventListener('keydown', handleKeydown);
    };

    onMounted(() => {
      if (props.defaultVisible || props.visible) {
        nextTick(showTooltip);
      }
    });

    onBeforeUnmount(() => {
      clearTimers();
      removeGlobalListeners();
    });

    watch(
      () => props.visible,
      (val) => {
        if (val && !innerVisible.value) {
          showTooltip();
        } else if (!val && innerVisible.value) {
          hideTooltip();
        }
      },
    );

    return {
      n,
      classes,
      triggerRef,
      tooltipRef,
      innerVisible,
      currentPosition,
      animClass,
      contentStyle,
      arrowStyle,
      showTooltip,
      hideTooltip,
      handleClose,
    };
  },
});
</script>
